import { captureEvent } from "@Light/app/analytics";
import { useFeature } from "@Light/app/features";
import {
  useMarkOptOutRatePageSeen,
  useOptionalOptOutSurvey,
  useOptOutPlanTracking,
} from "@Light/components/optOutSurvey";
import {
  getCreditFocusPresentation,
  RateFaqs,
  RateOffer,
} from "@Light/components/rateOffer";
import { useScaffold } from "@Light/scaffold";
import { AcceptedBy } from "@Light/services/lightTypes";
import { useOptionalRate, usePlan } from "@Light/utils/plan";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { getAcceptPlanErrorMessage } from "../acceptPlanError";
import { useEnrollment } from "../flow/context";
import { maybeRequirementValue } from "../flow/screen";
import { planPatch } from "../PlanSelection";
import { useEnrollmentUtilityConfiguration } from "../useEnrollmentUtilityConfiguration";
import { enrollmentFaqs } from "./enrollmentFaqs";

export type EnrollRateOfferProps = {
  terms: number[];
  selectedTerm: number;
  onSelectTerm: (term: number) => void;
};

export const EnrollRateOffer = ({
  terms,
  selectedTerm,
  onSelectTerm,
}: EnrollRateOfferProps) => {
  const { back, next, nextMutation, requirements } = useEnrollment();
  const rate = useOptionalRate();
  const { group, features, switchGroup } = usePlan();
  const [originalGroupUUID] = useState(group.uuid);
  const scaffold = useScaffold();
  const utilityBehavior = useEnrollmentUtilityConfiguration();
  useMarkOptOutRatePageSeen();
  useOptOutPlanTracking(rate?.uuid);
  const optOutSurvey = useOptionalOptOutSurvey();
  const isCreditFocusEnabled = useFeature(
    "CUSTOMER_CLIENT_CREDIT_FOCUSED_RATE",
  );
  const creditFocus =
    isCreditFocusEnabled && rate ? getCreditFocusPresentation(rate) : undefined;

  const acceptPlanError = nextMutation.isError
    ? getAcceptPlanErrorMessage(
        nextMutation.error as FetchBaseQueryError | undefined,
      )
    : null;

  const handleContinue = () => {
    if (!rate) return;
    if (group.uuid !== originalGroupUUID) {
      captureEvent("alternative_plan_continued", {
        from_plan_group_uuid: originalGroupUUID,
        to_plan_group_uuid: group.uuid,
      });
    }
    void next({
      ...planPatch(
        group.uuid,
        maybeRequirementValue(requirements.plan)?.group_uuid,
      ),
      acceptance: {
        rate_uuid: rate.uuid,
        terms_accepted: false,
        accepted_by: AcceptedBy.Customer,
      },
    });
  };

  return (
    <RateOffer
      rate={rate}
      features={features}
      planName={(rate ?? group).name}
      terms={terms}
      selectedTerm={selectedTerm}
      onSelectTerm={onSelectTerm}
      onContinue={handleContinue}
      mutation={nextMutation}
      continueButtonText={
        isCreditFocusEnabled ? "Continue with plan" : "Continue"
      }
      continueDisabled={!rate}
      errorMessage={acceptPlanError}
      onBack={back}
      faqs={<RateFaqs faqs={enrollmentFaqs(scaffold.page.companyName)} />}
      alternativePlans={group.alternative_plans}
      onSelectAlternativePlan={
        switchGroup &&
        ((alternativePlan) => {
          captureEvent("alternative_plan_clicked", {
            from_plan_group_uuid: group.uuid,
            to_plan_group_uuid: alternativePlan.plan_group_uuid,
          });
          switchGroup(alternativePlan.plan_group_uuid);
        })
      }
      utilityBehavior={utilityBehavior}
      creditFocus={creditFocus}
      inlineSupport={isCreditFocusEnabled}
      onMaybeLater={
        isCreditFocusEnabled && optOutSurvey
          ? () => {
              optOutSurvey.requestClose("rate_maybe_later");
            }
          : undefined
      }
    />
  );
};
